import { computed, type Ref } from 'vue';

export interface PriceCalculationParams {
    tripType: Ref<'oneway' | 'roundtrip'>;
    from: Ref<string>;
    dropTo: Ref<string>;
    passengers: Ref<number>;
    timeOneWay?: Ref<string>;
    timeArrival?: Ref<string>;
    timeDeparture?: Ref<string>;
}

const NIGHT_HOURS = { START: 22, END: 6 };
const PRICE_CONFIG = {
    BASE_ROUNDTRIP: 80,
    BASE_ONEWAY: 40,
    EXTRA_PASSENGER_ROUNDTRIP: 10,
    EXTRA_PASSENGER_ONEWAY: 5,
    ORLY_SURCHARGE: 15,
    NIGHT_SURCHARGE: 20,
};

/**
 * Composable pour calculer les prix de trajet selon les critères suivants :
 * 1) 1 personne = 80€ pour aller-retour (40€ juste arrivée/départ)
 * 2) +5€ par personne supplémentaire (roundtrip: +10€)
 * 3) +15€ si aéroport d'Orly
 * 4) +20€ si trajet(s) de nuit (22h00 - 06h00)
 */
export function usePriceCalculation({ tripType, from, dropTo, passengers, timeOneWay, timeArrival, timeDeparture }: PriceCalculationParams) {
    // Helper: Check if a time is during night hours
    const isNightTime = (time: string): boolean => {
        if (!time) return false;
        const hours = parseInt(time.split(':')[0], 10);
        return hours >= NIGHT_HOURS.START || hours < NIGHT_HOURS.END;
    };

    // Check if any trip leg is at night
    const hasNightTrip = computed(() => {
        if (tripType.value === 'oneway') {
            return timeOneWay ? isNightTime(timeOneWay.value) : false;
        }
        const arrivalIsNight = timeArrival ? isNightTime(timeArrival.value) : false;
        const departureIsNight = timeDeparture ? isNightTime(timeDeparture.value) : false;
        return arrivalIsNight || departureIsNight;
    });

    // Check if either location is Orly airport
    const hasOrly = computed(() => {
        return from.value.includes('ory') || dropTo.value.includes('ory');
    });

    // Calculate base price
    const basePrice = computed(() => {
        const isRoundTrip = tripType.value === 'roundtrip';
        const baseAmount = isRoundTrip ? PRICE_CONFIG.BASE_ROUNDTRIP : PRICE_CONFIG.BASE_ONEWAY;
        const extraPassengerRate = isRoundTrip ? PRICE_CONFIG.EXTRA_PASSENGER_ROUNDTRIP : PRICE_CONFIG.EXTRA_PASSENGER_ONEWAY;

        const extraPassengers = Math.max(0, passengers.value - 1);
        const priceWithPassengers = baseAmount + extraPassengers * extraPassengerRate;
        const priceWithOrly = hasOrly.value ? priceWithPassengers + PRICE_CONFIG.ORLY_SURCHARGE : priceWithPassengers;

        return priceWithOrly;
    });

    // Price with night surcharge
    const nightPrice = computed(() => basePrice.value + PRICE_CONFIG.NIGHT_SURCHARGE);

    // Final price based on whether it's a night trip
    const finalPrice = computed(() => (hasNightTrip.value ? nightPrice.value : basePrice.value));

    // Display formats
    const priceDisplay = computed(() => `${finalPrice.value} €`);
    const priceDisplayQuickPrice = computed(() => `${basePrice.value} € ~ ${nightPrice.value} €`);

    return {
        basePrice,
        nightPrice,
        finalPrice,
        hasNightTrip,
        priceDisplay,
        priceDisplayQuickPrice,
    };
}
